/**
 * Document Collaboration Store
 * Manages real-time collaboration state for documents using Zustand
 * Integrates with DMSManager and Lexical editor
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  CollaborationState, 
  CollaborationUser, 
  DocumentComment, 
  DocumentChange
} from '../../edgedocs/types/Collaboration.types';

// Enhanced interfaces for document collaboration
export interface UserInvitation {
  email: string;
  mobile?: string;
  role: 'editor' | 'reviewer' | 'viewer';
  message?: string;
  expiresIn?: number; // hours
}

export interface CollaborationWebSocketConnection {
  documentId: string;
  connection: WebSocket | null;
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastHeartbeat?: Date;
  reconnectAttempts: number;
}

export interface DocumentCollaborationState extends CollaborationState {
  // Enhanced state for document-specific collaboration
  isOwner: boolean;
  documentTitle?: string;
  documentVersionId?: string;
  lastSyncTimestamp?: Date;
  
  // Real-time connection state
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  
  // UI state
  showInviteModal: boolean;
  showCollaborationPanel: boolean;
  selectedCollaborators: string[];
  
  // Activity tracking
  recentActivity: Array<{
    id: string;
    type: string;
    user: string;
    description: string;
    timestamp: Date;
  }>;
}

export interface DocumentCollaborationStore {
  // Document-specific collaboration states
  documentCollaborations: Record<string, DocumentCollaborationState>;
  
  // WebSocket connections
  activeConnections: Record<string, CollaborationWebSocketConnection>;
  
  // User management
  currentUser: CollaborationUser | null;
  
  // Global UI state
  globalCollaborationEnabled: boolean;
  
  // Initialization
  initializeCollaboration: (documentId: string, documentTitle?: string, versionId?: string) => Promise<void>;
  
  // User Management Actions
  inviteUser: (documentId: string, invitation: UserInvitation, token?: string) => Promise<void>;
  updateUserPermissions: (documentId: string, userId: string, role: CollaborationUser['role']) => Promise<void>;
  removeUser: (documentId: string, userId: string) => Promise<void>;
  setCurrentUser: (user: CollaborationUser) => void;
  
  // Real-time Connection Management
  connectToDocument: (documentId: string) => Promise<void>;
  disconnectFromDocument: (documentId: string) => void;
  reconnectToDocument: (documentId: string) => Promise<void>;
  
  // Comments Management
  addComment: (documentId: string, comment: Omit<DocumentComment, 'id' | 'timestamp'>) => Promise<void>;
  editComment: (documentId: string, commentId: string, newText: string) => Promise<void>;
  resolveComment: (documentId: string, commentId: string) => Promise<void>;
  deleteComment: (documentId: string, commentId: string) => Promise<void>;
  replyToComment: (documentId: string, parentCommentId: string, reply: Omit<DocumentComment, 'id' | 'timestamp' | 'replies'>) => Promise<void>;
  
  // Track Changes Management
  toggleTrackChanges: (documentId: string) => void;
  addChange: (documentId: string, change: Omit<DocumentChange, 'id' | 'timestamp'>) => Promise<void>;
  acceptChange: (documentId: string, changeId: string) => Promise<void>;
  rejectChange: (documentId: string, changeId: string) => Promise<void>;
  acceptAllChanges: (documentId: string) => Promise<void>;
  rejectAllChanges: (documentId: string) => Promise<void>;
  
  // UI State Management
  setShowInviteModal: (documentId: string, show: boolean) => void;
  setShowCollaborationPanel: (documentId: string, show: boolean) => void;
  toggleCollaboratorSelection: (documentId: string, userId: string) => void;
  clearSelectedCollaborators: (documentId: string) => void;
  
  // Activity and Status
  updateUserActivity: (documentId: string, userId: string, activity: string) => void;
  getCollaborationStatus: (documentId: string) => {
    totalCollaborators: number;
    activeCollaborators: number;
    unreadComments: number;
    pendingChanges: number;
    lastActivity?: Date;
  };
  
  // Cleanup
  cleanupCollaboration: (documentId: string) => void;
  cleanupAllCollaborations: () => void;
}

// Version-aware real-time presence tracking
interface PresenceData {
  attorney: {
    id: string;
    name: string;
    email: string;
  };
  status: 'active' | 'idle' | 'offline';
  lastActivity: Date;
  currentSection: string;
  isTyping: boolean;
  caretPosition: number;
  selection?: any;
  // Version-specific fields
  documentId?: string;
  versionId?: string;
  versionNumber?: string;
}

// Version-aware global presence tracker for real-time updates
class PresenceTracker {
  private static instance: PresenceTracker;
  private presenceMap = new Map<string, PresenceData>(); // Key: userId:documentId:versionId
  private listeners = new Set<(userId: string, presence: PresenceData) => void>();

  static getInstance(): PresenceTracker {
    if (!PresenceTracker.instance) {
      PresenceTracker.instance = new PresenceTracker();
      PresenceTracker.instance.initialize();
    }
    return PresenceTracker.instance;
  }

  private initialize(): void {
    // Listen to EdgeDocs presence events (version-aware)
    window.addEventListener('edgedocs-presence-update', (event: any) => {
      const presenceData: PresenceData = event.detail;
      const userId = presenceData.attorney.id;
      
      // Create version-specific key
      const versionKey = this.createVersionKey(userId, presenceData.documentId, presenceData.versionId);
      
      console.log('🔄 [PresenceTracker] Received version-aware presence update:', {
        userId,
        documentId: presenceData.documentId,
        versionId: presenceData.versionId,
        status: presenceData.status,
        lastActivity: presenceData.lastActivity,
        versionKey
      });
      
      this.presenceMap.set(versionKey, presenceData);
      
      // Also maintain a user-level key for backward compatibility
      this.presenceMap.set(userId, presenceData);
      
      // Notify all listeners
      this.listeners.forEach(listener => {
        listener(userId, presenceData);
      });
    });

    console.log('✅ [PresenceTracker] Initialized version-aware real-time presence tracking');
  }

  private createVersionKey(userId: string, documentId?: string, versionId?: string): string {
    if (documentId && versionId) {
      return `${userId}:${documentId}:${versionId}`;
    }
    return userId; // Fallback to user ID only
  }

  public addListener(callback: (userId: string, presence: PresenceData) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  public getPresence(userId: string): PresenceData | null {
    return this.presenceMap.get(userId) || null;
  }

  public getVersionPresence(userId: string, documentId: string, versionId: string): PresenceData | null {
    const versionKey = this.createVersionKey(userId, documentId, versionId);
    return this.presenceMap.get(versionKey) || this.getPresence(userId); // Fallback to user-level
  }

  public getAllPresence(): Map<string, PresenceData> {
    return new Map(this.presenceMap);
  }

  public getVersionCollaborators(documentId: string, versionId: string): PresenceData[] {
    const versionCollaborators: PresenceData[] = [];
    
    this.presenceMap.forEach((presence, key) => {
      if (key.includes(`${documentId}:${versionId}`)) {
        versionCollaborators.push(presence);
      }
    });
    
    return versionCollaborators;
  }
}

// Default collaboration state
const createDefaultCollaborationState = (documentId: string, isOwner = false): DocumentCollaborationState => {
  // Mark documentId as used for future document-specific initialization
  console.log(`[CollaborationStore] Creating default state for document ${documentId}`);
  
  return {
  trackChangesEnabled: false,
  pendingChanges: [],
  comments: [],
  showCommentsPanel: false,
  collaborators: [],
  currentUser: null,
  documentPermissions: {
    isPublic: false,
    allowAnonymousComments: false,
    requireApprovalForChanges: true,
  },
  
  // Enhanced state
  isOwner,
  connectionStatus: 'disconnected',
  showInviteModal: false,
  showCollaborationPanel: true,
  selectedCollaborators: [],
  recentActivity: [],
  };
};

// Permission matrix for role-based access
const ROLE_PERMISSIONS: Record<CollaborationUser['role'], CollaborationUser['permissions']> = {
  owner: {
    canEdit: true,
    canComment: true,
    canTrackChanges: true,
    canAcceptReject: true,
    canShare: true,
    canManageUsers: true,
  },
  editor: {
    canEdit: true,
    canComment: true,
    canTrackChanges: true,
    canAcceptReject: false,
    canShare: false,
    canManageUsers: false,
  },
  reviewer: {
    canEdit: false,
    canComment: true,
    canTrackChanges: false,
    canAcceptReject: false,
    canShare: false,
    canManageUsers: false,
  },
  viewer: {
    canEdit: false,
    canComment: false,
    canTrackChanges: false,
    canAcceptReject: false,
    canShare: false,
    canManageUsers: false,
  },
};

// Main store implementation
export const useDocumentCollaborationStore = create<DocumentCollaborationStore>()(
  subscribeWithSelector((set, get) => {
    // Initialize presence tracker for real-time updates
    const presenceTracker = PresenceTracker.getInstance();
    
    // Listen to real-time presence updates
    presenceTracker.addListener((userId: string, presence: PresenceData) => {
      const state = get();
      
      // Update lastActive for all documents where this user is a collaborator
      Object.keys(state.documentCollaborations).forEach(documentId => {
        const docState = state.documentCollaborations[documentId];
        const collaboratorIndex = docState.collaborators.findIndex(c => c.id === userId);
        
        if (collaboratorIndex !== -1) {
          console.log(`🔄 [CollaborationStore] Updating real-time presence for user ${userId} in document ${documentId}`);
          
          set(state => ({
            documentCollaborations: {
              ...state.documentCollaborations,
              [documentId]: {
                ...state.documentCollaborations[documentId],
                collaborators: state.documentCollaborations[documentId].collaborators.map((collaborator, index) =>
                  index === collaboratorIndex
                    ? { ...collaborator, lastActive: presence.lastActivity }
                    : collaborator
                )
              }
            }
          }));
        }
      });
    });

    return {
    documentCollaborations: {},
    activeConnections: {},
    currentUser: null,
    globalCollaborationEnabled: true,

    initializeCollaboration: async (documentId: string, documentTitle?: string, versionId?: string) => {
      try {
        console.log(`🚀 [CollaborationStore] Initializing collaboration for document: ${documentId}`);
        
        // Check if already initialized
        const existingState = get().documentCollaborations[documentId];
        if (existingState) {
          console.log(`ℹ️ [CollaborationStore] Document ${documentId} already initialized`);
          return;
        }

        // Load existing collaboration data from API
        // Note: This store function doesn't have direct access to keycloak
        // The component calling this should pass the token or handle the API call
        console.warn('[CollaborationStore] Token access needed - component should handle API calls');
        
        // For now, skip the API call in the store - let components handle it
        console.log(`ℹ️ [CollaborationStore] Skipping API call, will be handled by component`);
        const collaborationData: Partial<DocumentCollaborationState> = {};
        
        // First time collaboration setup (components will handle API calls)
        console.log(`📝 [CollaborationStore] Creating default collaboration state for ${documentId}`);

        // Determine if current user is owner (stub implementation)
        const isOwner = true; // TODO: Get from actual user permissions
        
        // Create initial collaboration state
        const initialState: DocumentCollaborationState = {
          ...createDefaultCollaborationState(documentId, isOwner),
          ...collaborationData,
          documentTitle,
          documentVersionId: versionId,
          lastSyncTimestamp: new Date(),
        };

        // Update store
        set(state => ({
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: initialState,
          },
        }));

        // Connect to real-time updates
        // TODO: Re-enable when WebSocket backend is implemented
        // await get().connectToDocument(documentId);
        console.log(`ℹ️ [CollaborationStore] WebSocket connections temporarily disabled until backend implementation`);
        
        console.log(`🎉 [CollaborationStore] Collaboration initialized successfully for ${documentId}`);
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to initialize collaboration for ${documentId}:`, error);
        // Set error state but still allow basic functionality
        set(state => ({
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...createDefaultCollaborationState(documentId),
              connectionStatus: 'error',
            },
          },
        }));
      }
    },

    connectToDocument: async (documentId: string) => {
      try {
        const existingConnection = get().activeConnections[documentId];
        if (existingConnection?.connection?.readyState === WebSocket.OPEN) {
          console.log(`ℹ️ [CollaborationStore] Already connected to document ${documentId}`);
          return;
        }

        console.log(`🔌 [CollaborationStore] Connecting to document ${documentId}`);
        
        // Update connection status
        set(state => ({
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...state.documentCollaborations[documentId],
              connectionStatus: 'connecting',
            },
          },
        }));

        // Create WebSocket connection
        const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:3001'}/documents/${documentId}/collaboration`;
        const ws = new WebSocket(wsUrl);
        
        // Set up event handlers
        ws.onopen = () => {
          console.log(`✅ [CollaborationStore] Connected to collaboration for document ${documentId}`);
          
          set(state => ({
            documentCollaborations: {
              ...state.documentCollaborations,
              [documentId]: {
                ...state.documentCollaborations[documentId],
                connectionStatus: 'connected',
              },
            },
            activeConnections: {
              ...state.activeConnections,
              [documentId]: {
                documentId,
                connection: ws,
                status: 'connected',
                lastHeartbeat: new Date(),
                reconnectAttempts: 0,
              },
            },
          }));
        };

        ws.onmessage = (event) => {
          try {
            const collaborationEvent = JSON.parse(event.data);
            handleCollaborationEvent(documentId, collaborationEvent);
          } catch (error) {
            console.error(`❌ [CollaborationStore] Failed to parse WebSocket message:`, error);
          }
        };

        ws.onclose = (event) => {
          console.log(`🔌 [CollaborationStore] Disconnected from collaboration for document ${documentId}:`, event.reason);
          
          set(state => ({
            documentCollaborations: {
              ...state.documentCollaborations,
              [documentId]: {
                ...state.documentCollaborations[documentId],
                connectionStatus: 'disconnected',
              },
            },
          }));

          // Attempt reconnection if not intentional
          if (event.code !== 1000) {
            setTimeout(() => {
              get().reconnectToDocument(documentId);
            }, 3000);
          }
        };

        ws.onerror = (error) => {
          console.error(`❌ [CollaborationStore] WebSocket error for document ${documentId}:`, error);
          
          set(state => ({
            documentCollaborations: {
              ...state.documentCollaborations,
              [documentId]: {
                ...state.documentCollaborations[documentId],
                connectionStatus: 'error',
              },
            },
          }));
        };

        // Store the connection
        set(state => ({
          activeConnections: {
            ...state.activeConnections,
            [documentId]: {
              documentId,
              connection: ws,
              status: 'connecting',
              lastHeartbeat: new Date(),
              reconnectAttempts: 0,
            },
          },
        }));

      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to connect to document ${documentId}:`, error);
        
        set(state => ({
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...state.documentCollaborations[documentId],
              connectionStatus: 'error',
            },
          },
        }));
      }
    },

    disconnectFromDocument: (documentId: string) => {
      const connection = get().activeConnections[documentId];
      if (connection?.connection) {
        connection.connection.close(1000, 'User disconnected');
        console.log(`🔌 [CollaborationStore] Disconnected from document ${documentId}`);
      }

      set(state => {
        const newConnections = { ...state.activeConnections };
        delete newConnections[documentId];
        
        return {
          activeConnections: newConnections,
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...state.documentCollaborations[documentId],
              connectionStatus: 'disconnected',
            },
          },
        };
      });
    },

    reconnectToDocument: async (documentId: string) => {
      const connection = get().activeConnections[documentId];
      if (connection && connection.reconnectAttempts < 5) {
        console.log(`🔄 [CollaborationStore] Attempting to reconnect to document ${documentId} (attempt ${connection.reconnectAttempts + 1})`);
        
        set(state => ({
          activeConnections: {
            ...state.activeConnections,
            [documentId]: {
              ...connection,
              reconnectAttempts: connection.reconnectAttempts + 1,
            },
          },
        }));

        await get().connectToDocument(documentId);
      } else {
        console.error(`❌ [CollaborationStore] Max reconnection attempts reached for document ${documentId}`);
      }
    },

    inviteUser: async (documentId: string, invitation: UserInvitation, token?: string) => {
      try {
        console.log(`📧 [CollaborationStore] Inviting user ${invitation.email} to document ${documentId}`);
        console.log(`🔑 [CollaborationStore] Token provided:`, token ? 'YES' : 'NO');
        console.log(`🔑 [CollaborationStore] Token length:`, token ? token.length : 'N/A');
        
        // Get token from parameter or fallback to localStorage (for backward compatibility)
        const authToken = token || localStorage.getItem('token');
        console.log(`🔑 [CollaborationStore] Using token source:`, token ? 'parameter' : 'localStorage');
        console.log(`🔑 [CollaborationStore] Final token length:`, authToken ? authToken.length : 'N/A');
        
        if (!authToken) {
          throw new Error('No authentication token available');
        }
        
        const response = await fetch(`/api/v1/documents/${documentId}/collaborators/invite`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...invitation,
            permissions: ROLE_PERMISSIONS[invitation.role],
          }),
        });

        if (response.ok) {
          const invitationResult = await response.json();
          console.log(`✅ [CollaborationStore] User invited successfully:`, invitationResult);
          
          // Add activity log
          get().updateUserActivity(documentId, get().currentUser?.id || 'system', `Invited ${invitation.email} as ${invitation.role}`);
          
          // Close invite modal
          get().setShowInviteModal(documentId, false);
        } else {
          const error = await response.json();
          throw new Error(error.message || 'Failed to send invitation');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to invite user:`, error);
        throw error;
      }
    },

    updateUserPermissions: async (documentId: string, userId: string, role: CollaborationUser['role']) => {
      try {
        console.log(`🔐 [CollaborationStore] Updating user ${userId} permissions to ${role} for document ${documentId}`);
        
        const response = await fetch(`/api/v1/documents/${documentId}/collaborators/${userId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            role,
            permissions: ROLE_PERMISSIONS[role],
          }),
        });

        if (response.ok) {
          // Update local state
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  collaborators: docState.collaborators.map(user =>
                    user.id === userId
                      ? { ...user, role, permissions: ROLE_PERMISSIONS[role] }
                      : user
                  ),
                },
              },
            };
          });

          console.log(`✅ [CollaborationStore] User permissions updated successfully`);
        } else {
          throw new Error('Failed to update user permissions');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to update user permissions:`, error);
        throw error;
      }
    },

    removeUser: async (documentId: string, userId: string) => {
      try {
        console.log(`🗑️ [CollaborationStore] Removing user ${userId} from document ${documentId}`);
        
        const response = await fetch(`/api/v1/documents/${documentId}/collaborators/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          // Update local state
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  collaborators: docState.collaborators.filter(user => user.id !== userId),
                },
              },
            };
          });

          console.log(`✅ [CollaborationStore] User removed successfully`);
        } else {
          throw new Error('Failed to remove user');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to remove user:`, error);
        throw error;
      }
    },

    setCurrentUser: (user: CollaborationUser) => {
      set({ currentUser: user });
      console.log(`👤 [CollaborationStore] Current user set:`, user.email);
    },

    addComment: async (documentId: string, comment: Omit<DocumentComment, 'id' | 'timestamp'>) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/comments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(comment),
        });

        if (response.ok) {
          const newComment = await response.json();
          
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  comments: [...docState.comments, newComment],
                },
              },
            };
          });

          console.log(`💬 [CollaborationStore] Comment added successfully`);
        } else {
          throw new Error('Failed to add comment');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to add comment:`, error);
        throw error;
      }
    },

    editComment: async (documentId: string, commentId: string, newText: string) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/comments/${commentId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content: newText }),
        });

        if (response.ok) {
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  comments: docState.comments.map(comment =>
                    comment.id === commentId
                      ? { ...comment, text: newText, edited: true, editTimestamp: new Date() }
                      : comment
                  ),
                },
              },
            };
          });

          console.log(`✏️ [CollaborationStore] Comment edited successfully`);
        } else {
          throw new Error('Failed to edit comment');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to edit comment:`, error);
        throw error;
      }
    },

    resolveComment: async (documentId: string, commentId: string) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/comments/${commentId}/resolve`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  comments: docState.comments.map(comment =>
                    comment.id === commentId
                      ? { ...comment, resolved: true }
                      : comment
                  ),
                },
              },
            };
          });

          console.log(`✅ [CollaborationStore] Comment resolved successfully`);
        } else {
          throw new Error('Failed to resolve comment');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to resolve comment:`, error);
        throw error;
      }
    },

    deleteComment: async (documentId: string, commentId: string) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/comments/${commentId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  comments: docState.comments.filter(comment => comment.id !== commentId),
                },
              },
            };
          });

          console.log(`🗑️ [CollaborationStore] Comment deleted successfully`);
        } else {
          throw new Error('Failed to delete comment');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to delete comment:`, error);
        throw error;
      }
    },

    replyToComment: async (documentId: string, parentCommentId: string, reply: Omit<DocumentComment, 'id' | 'timestamp' | 'replies'>) => {
      try {
        const replyData = {
          ...reply,
          parentCommentId,
        };

        const response = await fetch(`/api/v1/documents/${documentId}/comments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(replyData),
        });

        if (response.ok) {
          const newReply = await response.json();
          
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  comments: docState.comments.map(comment =>
                    comment.id === parentCommentId
                      ? {
                          ...comment,
                          replies: [...(comment.replies || []), newReply],
                        }
                      : comment
                  ),
                },
              },
            };
          });

          console.log(`💬 [CollaborationStore] Reply added successfully`);
        } else {
          throw new Error('Failed to add reply');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to add reply:`, error);
        throw error;
      }
    },

    toggleTrackChanges: (documentId: string) => {
      set(state => {
        const docState = state.documentCollaborations[documentId];
        if (!docState) return state;

        const newEnabled = !docState.trackChangesEnabled;
        console.log(`🔄 [CollaborationStore] Track changes ${newEnabled ? 'enabled' : 'disabled'} for document ${documentId}`);

        return {
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...docState,
              trackChangesEnabled: newEnabled,
            },
          },
        };
      });
    },

    addChange: async (documentId: string, change: Omit<DocumentChange, 'id' | 'timestamp'>) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/changes`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(change),
        });

        if (response.ok) {
          const newChange = await response.json();
          
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  pendingChanges: [...docState.pendingChanges, newChange],
                },
              },
            };
          });

          console.log(`📝 [CollaborationStore] Change tracked successfully`);
        } else {
          throw new Error('Failed to track change');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to track change:`, error);
        throw error;
      }
    },

    acceptChange: async (documentId: string, changeId: string) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/changes/${changeId}/accept`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  pendingChanges: docState.pendingChanges.map(change =>
                    change.id === changeId
                      ? { ...change, accepted: true }
                      : change
                  ),
                },
              },
            };
          });

          console.log(`✅ [CollaborationStore] Change accepted successfully`);
        } else {
          throw new Error('Failed to accept change');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to accept change:`, error);
        throw error;
      }
    },

    rejectChange: async (documentId: string, changeId: string) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/changes/${changeId}/reject`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  pendingChanges: docState.pendingChanges.map(change =>
                    change.id === changeId
                      ? { ...change, accepted: false }
                      : change
                  ),
                },
              },
            };
          });

          console.log(`❌ [CollaborationStore] Change rejected successfully`);
        } else {
          throw new Error('Failed to reject change');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to reject change:`, error);
        throw error;
      }
    },

    acceptAllChanges: async (documentId: string) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/changes/accept-all`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  pendingChanges: docState.pendingChanges.map(change => ({
                    ...change,
                    accepted: true,
                  })),
                },
              },
            };
          });

          console.log(`✅ [CollaborationStore] All changes accepted successfully`);
        } else {
          throw new Error('Failed to accept all changes');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to accept all changes:`, error);
        throw error;
      }
    },

    rejectAllChanges: async (documentId: string) => {
      try {
        const response = await fetch(`/api/v1/documents/${documentId}/changes/reject-all`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          set(state => {
            const docState = state.documentCollaborations[documentId];
            if (!docState) return state;

            return {
              documentCollaborations: {
                ...state.documentCollaborations,
                [documentId]: {
                  ...docState,
                  pendingChanges: docState.pendingChanges.map(change => ({
                    ...change,
                    accepted: false,
                  })),
                },
              },
            };
          });

          console.log(`❌ [CollaborationStore] All changes rejected successfully`);
        } else {
          throw new Error('Failed to reject all changes');
        }
      } catch (error) {
        console.error(`❌ [CollaborationStore] Failed to reject all changes:`, error);
        throw error;
      }
    },

    setShowInviteModal: (documentId: string, show: boolean) => {
      set(state => {
        const docState = state.documentCollaborations[documentId];
        if (!docState) return state;

        return {
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...docState,
              showInviteModal: show,
            },
          },
        };
      });
    },

    setShowCollaborationPanel: (documentId: string, show: boolean) => {
      set(state => {
        const docState = state.documentCollaborations[documentId];
        if (!docState) return state;

        return {
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...docState,
              showCollaborationPanel: show,
            },
          },
        };
      });
    },

    toggleCollaboratorSelection: (documentId: string, userId: string) => {
      set(state => {
        const docState = state.documentCollaborations[documentId];
        if (!docState) return state;

        const isSelected = docState.selectedCollaborators.includes(userId);
        const newSelection = isSelected
          ? docState.selectedCollaborators.filter(id => id !== userId)
          : [...docState.selectedCollaborators, userId];

        return {
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...docState,
              selectedCollaborators: newSelection,
            },
          },
        };
      });
    },

    clearSelectedCollaborators: (documentId: string) => {
      set(state => {
        const docState = state.documentCollaborations[documentId];
        if (!docState) return state;

        return {
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...docState,
              selectedCollaborators: [],
            },
          },
        };
      });
    },

    updateUserActivity: (documentId: string, userId: string, activity: string) => {
      set(state => {
        const docState = state.documentCollaborations[documentId];
        if (!docState) return state;

        const newActivity = {
          id: `activity-${Date.now()}`,
          type: 'user_activity',
          user: userId,
          description: activity,
          timestamp: new Date(),
        };

        return {
          documentCollaborations: {
            ...state.documentCollaborations,
            [documentId]: {
              ...docState,
              recentActivity: [newActivity, ...docState.recentActivity.slice(0, 49)], // Keep last 50 activities
            },
          },
        };
      });
    },

    getCollaborationStatus: (documentId: string) => {
      const docState = get().documentCollaborations[documentId];
      if (!docState) {
        return {
          totalCollaborators: 0,
          activeCollaborators: 0,
          unreadComments: 0,
          pendingChanges: 0,
        };
      }

      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
      const presenceTracker = PresenceTracker.getInstance();

      // Enhanced version-aware active collaborator detection using real-time presence
      const activeCollaborators = docState.collaborators.filter(user => {
        // First check version-specific real-time presence data
        // Note: We don't have versionId in this context, so we'll use general presence
        // This method is used by the overall collaboration system
        const presence = presenceTracker.getPresence(user.id);
        if (presence) {
          const isRecentlyActive = new Date(presence.lastActivity) > fiveMinutesAgo;
          const isActiveStatus = presence.status === 'active' || presence.status === 'idle';
          
          console.log(`🔍 [CollaborationStatus] User ${user.name}: presence=${presence.status}, lastActivity=${presence.lastActivity}, documentId=${presence.documentId}, versionId=${presence.versionId}, isActive=${isRecentlyActive && isActiveStatus}`);
          
          return isRecentlyActive && isActiveStatus;
        }
        
        // Fallback to stored lastActive timestamp
        if (!user.lastActive) {
          console.log(`🔍 [CollaborationStatus] User ${user.name}: no lastActive timestamp, inactive`);
          return false;
        }
        
        const lastActiveDate = user.lastActive instanceof Date ? user.lastActive : new Date(user.lastActive);
        const fallbackActive = lastActiveDate > fiveMinutesAgo;
        console.log(`🔍 [CollaborationStatus] User ${user.name}: fallback lastActive=${user.lastActive}, isActive=${fallbackActive}`);
        
        return fallbackActive;
      });

      const status = {
        totalCollaborators: docState.collaborators.length,
        activeCollaborators: activeCollaborators.length,
        unreadComments: docState.comments.filter(comment => !comment.resolved).length,
        pendingChanges: docState.pendingChanges.filter(change => change.accepted === null).length,
        lastActivity: docState.recentActivity[0]?.timestamp,
      };

      console.log(`📊 [CollaborationStatus] Document ${documentId}:`, status);
      
      return status;
    },

    cleanupCollaboration: (documentId: string) => {
      // Disconnect WebSocket
      get().disconnectFromDocument(documentId);

      // Remove from store
      set(state => {
        const newCollaborations = { ...state.documentCollaborations };
        delete newCollaborations[documentId];

        return {
          documentCollaborations: newCollaborations,
        };
      });

      console.log(`🧹 [CollaborationStore] Cleaned up collaboration for document ${documentId}`);
    },

    cleanupAllCollaborations: () => {
      const documentIds = Object.keys(get().documentCollaborations);
      
      documentIds.forEach(documentId => {
        get().disconnectFromDocument(documentId);
      });

      set({
        documentCollaborations: {},
        activeConnections: {},
      });

      console.log(`🧹 [CollaborationStore] Cleaned up all collaborations`);
    },
  };
  })
);

// Helper function to handle WebSocket events
function handleCollaborationEvent(documentId: string, event: any) {
  console.log(`📨 [CollaborationStore] Received event for document ${documentId}:`, event.type);

  switch (event.type) {
    case 'user-joined':
      // Add user to collaborators list
      break;
    
    case 'user-left':
      // Remove user from collaborators list
      break;
    
    case 'comment-added':
      // Add new comment to state
      break;
    
    case 'change-tracked':
      // Add new change to pending changes
      break;
    
    // Handle other event types...
    default:
      console.log(`🤷 [CollaborationStore] Unknown event type: ${event.type}`);
  }
}

// Export a hook for easy component usage
export const useDocumentCollaboration = (documentId: string) => {
  const store = useDocumentCollaborationStore();
  const documentState = store.documentCollaborations[documentId];
  
  return {
    state: documentState,
    actions: {
      initialize: () => store.initializeCollaboration(documentId),
      connect: () => store.connectToDocument(documentId),
      disconnect: () => store.disconnectFromDocument(documentId),
      inviteUser: (invitation: UserInvitation, token?: string) => store.inviteUser(documentId, invitation, token),
      addComment: (comment: Omit<DocumentComment, 'id' | 'timestamp'>) => store.addComment(documentId, comment),
      toggleTrackChanges: () => store.toggleTrackChanges(documentId),
      setShowInviteModal: (show: boolean) => store.setShowInviteModal(documentId, show),
    },
    status: store.getCollaborationStatus(documentId),
    isConnected: (() => {
      // Check if WebSocket is connected via existing connection status
      const connectionStatus = store.activeConnections[documentId]?.status === 'connected';
      
      // Also check if EdgeDocs WebSocket is connected (for documents being edited)
      const presenceTracker = PresenceTracker.getInstance();
      const hasRecentPresence = Array.from(presenceTracker.getAllPresence().values()).some(presence => 
        new Date(presence.lastActivity) > new Date(Date.now() - 30000) // Active within 30 seconds
      );
      
      const isConnected = connectionStatus || hasRecentPresence;
      
      console.log(`🔗 [CollaborationStore] Connection status for ${documentId}: connection=${connectionStatus}, hasRecentPresence=${hasRecentPresence}, isConnected=${isConnected}`);
      
      return isConnected;
    })(),
  };
};

export default useDocumentCollaborationStore; 